import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import {
  DirectionFormDto,
  type DirectionFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/direction/direction-form.dto';
import { AccountingSubAccountEnum } from '@zaoblako/shared/modules/catalog/enums/accounting-sub-account.enum';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import { DirectionTypeEnum } from '@zaoblako/shared/modules/catalog/enums/direction.type.enum';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { AccountingAccountSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/accounting-account-single-select/accounting-account-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UserAndGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-and-group-multi-select/user-and-group-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { AccountingSubTypeNamePipe } from '@zaoblako/web/shared/pipes/accounting-sub-type-name.pipe';
import { DirectionKindNamePipe } from '@zaoblako/web/shared/pipes/direction-kind-name.pipe';
import { DirectionTypeNamePipe } from '@zaoblako/web/shared/pipes/direction-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-direction-form',
  templateUrl: './direction-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SingleSelectComponent,
    DirectionTypeNamePipe,
    DirectionKindNamePipe,
    DirectionSingleSelectComponent,
    CheckboxModule,
    TagModule,
    FormsModule,
    ButtonModule,
    InputNumberModule,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    AccountingSubTypeNamePipe,
    AccountingAccountSingleSelectComponent,
    UserAndGroupMultiSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DirectionFormComponent extends BaseFormComponent<DirectionFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public direction = <DirectionFindInterface>{};

  public engine: 'class-transformer' | 'serde' = 'serde';

  public dto = DirectionFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];

  public types = Object.values(DirectionTypeEnum);
  public kinds = Object.values(DirectionKindEnum);
  public priorities = Object.values(HelpdeskTaskPriorityEnum);
  public accountingSubTypes = Object.values(AccountingSubAccountEnum).filter(
    (val) => val !== AccountingSubAccountEnum.BALANCE_TURNOVER
  );

  public canUploadAtrium = false;

  public users: UserFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public directionUsers = [];

  public get DirectionTypeEnum() {
    return DirectionTypeEnum;
  }

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly directionService: DirectionService,
    public readonly formStoreService: FormStoreService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService
  ) {
    super();
  }

  public ngOnInit() {
    this.canUploadAtrium = this.globalService.globalInfo?.tenantInfo?.isConnectedToAtrium;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.directionService.find<DirectionFindInterface>(),
      this.userService.findAll({ withBots: true }),
      this.userGroupService.find<UserGroupFindInterface>()
    ]).subscribe(([directions, users, userGroups]) => {
      this.directions = directions;
      this.users = users;
      this.userGroups = userGroups;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.directionService
      .findById<DirectionFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (direction) => {
          this.direction = direction;
          if (this.direction.users?.length) {
            this.direction.users.forEach((user) => this.directionUsers.push({ user }));
          }
          if (this.direction.userGroups?.length) {
            this.direction.userGroups.forEach((group) => this.directionUsers.push({ group }));
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDirection() {
    if (this.checkValidate(this.direction as DirectionFormInterface).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.directionService.update<DirectionFormInterface, DirectionFindInterface>(this.direction).subscribe({
          next: (direction) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Бюджетная статья "${this.direction.name}" обновлена`);
            this.formStoreService.saveNext(direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.directionService.create<DirectionFindInterface>(this.direction).subscribe({
          next: (direction) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Бюджетная статья создана');
            this.formStoreService.saveNext(direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDirection() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить бюджетную статью "${this.direction.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.directionService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Бюджетная статья "${this.direction.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreDirection() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить статью "${this.direction.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.directionService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.direction = { ...this.direction, deleted: false };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Статья "${this.direction.name}" восстановлена`);
            this.formStoreService.saveNext(this.direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public onChangeUsersAndGroup(): void {
    this.direction.users = [];
    this.direction.userGroups = [];
    this.directionUsers.forEach((directionUser) => {
      directionUser.user
        ? this.direction.users.push(directionUser.user)
        : this.direction.userGroups.push(directionUser.group);
    });
  }
}
